import React, { useState } from "react";

export interface Cell {
  value: number | null;
  isPreFilled: boolean;
  factText?: string;
}

export interface Fact {
  id: string;
  number: number;
  text: string;
  isPlaced: boolean;
}

export interface GridConfig {
  grid: number[][];
  rowOps: string[];
  colOps: string[];
  rowTotals: number[];
  colTotals: number[];
}

export const FACTS_DATABASE: { [key: number]: string[] } = {
  1: [
    "Free speech US amendment",
    "January month",
    "Unicorn horns",
    "What 'O' means in BOGO",
    "60 seconds equals this many minutes",
    "Cent value of a penny",
    "Hydrogen atomic number",
    "Earth's moons",
    "Cyclops eyes",
    "Uno",
    "Lives we have",
    "Wheels on a unicycle",
    "Gold medal rank",
    "Binary true value",
    "Solo",
    "Unibrow brows",
    "Slides on a trombone",
    "Small step for man, giant leap for mankind",
    "Human hearts",
    "Solo home run points",
    "George Washington president number",
    "Pitcher position number in baseball",
    "Basketball free throw point",
    "Strokes over par for bogey",
    "Strokes under par for a birdie",
    "What an ace is worth other than 11",
    "Direction (band)",
    "Republic (band)",
  ],
  2: [
    "Only even prime",
    "Duo",
    "Dos",
    "Silver medal rank",
    "Bars in standard Twix",
    "Derek Jeter number",
    "First loser",
    "Brain hemispheres",
    "Wings on a bird",
    "Peace sign fingers",
    "Wheels on a bicycle",
    "Bear arms amendment",
    "Football field goal",
    "John Adams president number",
    "Catcher position baseball",
    "Strokes under par for an eagle",
    "_ and a half men",
    "Number of world wars",
    "Cutting off a Hydra head grows this many more",
    "Lungs",
  ],
  3: [
    "Miles in a 5k",
    "Tres",
    "Objects part of the Deathly Hallows",
    "Lights on a traffic light",
    "Wheels on a tricycle",
    "First odd prime",
    "Sides on a triangle",
    "Trio",
    "Amigos",
    "Stooges",
    "Days grace (band)",
    "TKO knockdowns",
    "Movies in Star Wars original series",
    "Blind mice",
    "Events in an Ironman",
    "Octopus hearts",
    "Squid hearts",
    "Points a field goal is worth (Football)",
    "Thomas Jefferson president number",
    "Basketball points beyond the arc",
    "Trumpet valves",
    "Number of wise men",
    "Musketeers",
    "Doors down (band)",
    "Rule of _ (photography)",
  ],
  4: [
    "Mickey Mouse fingers",
    "Standard Kit Kat bar fingers",
    "Homer Simpson fingers",
    "Number of seasons",
    "Sides on a square",
    "Members of the Beatles",
    "Standard violin strings",
    "Wheels on a car",
    "Bases of DNA",
    "Brett Favre number",
    "Fantastic _ (superheroes)",
    "IV",
    "Heart chambers",
    "Bass guitar strings",
    "Years between olympics",
    "How many teats a cow has",
    "Quarts in a gallon",
    "Quarters in a football game",
    "Runs scored in a grand slam (baseball)",
    "Strings on a ukelele",
    "The ____ Seasons (band)",
    "Jupiter's main ring components",
  ],
  5: [
    "Human fingers",
    "Books in the Torah",
    "Cent value of a nickel",
    "V",
    "Slaughterhouse _ (book)",
    "Human toes",
    "Human senses",
    "Starfish sides",
    "Maroon (band)",
    "Jackson (band)",
    "Kobe Bryant NBA championships",
    "Soccer penalty shootout kicks",
    "Basketball team number on the court",
    "Stages of grief",
  ],
  6: [
    "Highest number standard die",
    "Hexagon sides",
    "Types of triangles",
    "Guitar strings",
    "Snowflake points",
    "Spatial directions",
    "Carbon atomic number",
    "Michael Jordan NBA championships",
    "Henry the 8th's wives",
    "Ant legs",
    "Electric guitar strings",
    "Points a touchdown is worth",
  ],
  7: [
    "Number of Larry King's wives",
    "Colors of the rainbow",
    "Days in which God created the earth",
    "Slide positions on a trombone",
    "Days in a week",
    "Lucky number",
    "Books in Harry Potter series",
    "Continents",
    "Common roll with 2 6-sided dice",
    "Deadly sins",
  ],
  8: [
    "Ounces in a cup",
    "Number of legs a spider has",
    "Planets in our solar system",
    "Eyes that spiders have",
    "Ocho",
    "VIII",
    "_ mile (movie)",
    "Hawaii main islands",
    "August month",
    "Sideways infinity symbol",
    "BAC limit for standard drivers in the US is .0_",
    "Record for Gold medals won by Michael Phelps in a single olympics",
  ],
  9: [
    "Number of cat lives",
    "Number of Santa's reindeer",
    "Members of the brady bunch",
    "Human pregnancy months",
    "Beethoven's symphonies",
    "Justices on the supreme court",
    "Magic number",
    "IX",
    "District _ (movie)",
    "September month",
    "Innings in a baseball game",
    "Octopus brains",
    "Nonagon sides",
    "Fluorine element periodic table",
    "Sudoku grids are _x_",
    "Largest single digit number",
    "Rubik's cube stickers per face",
  ],
};

function generateRandomPuzzle(): GridConfig {
  // Generate 9 unique numbers from 1-9
  const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  const shuffled = numbers.sort(() => Math.random() - 0.5);

  const grid = [
    [shuffled[0], shuffled[1], shuffled[2]],
    [shuffled[3], shuffled[4], shuffled[5]],
    [shuffled[6], shuffled[7], shuffled[8]],
  ];

  // Randomly assign + or - operations
  const rowOps = Array.from({ length: 6 }, () => (Math.random() > 0.5 ? "+" : "-"));
  const colOps = Array.from({ length: 6 }, () => (Math.random() > 0.5 ? "+" : "-"));

  // Calculate totals based on operations
  const rowTotals = grid.map((row, i) => {
    let total = row[0];
    total = rowOps[i * 2] === "+" ? total + row[1] : total - row[1];
    total = rowOps[i * 2 + 1] === "+" ? total + row[2] : total - row[2];
    return total;
  });

  const colTotals = [0, 1, 2].map((col) => {
    let total = grid[0][col];
    total = colOps[col * 2] === "+" ? total + grid[1][col] : total - grid[1][col];
    total = colOps[col * 2 + 1] === "+" ? total + grid[2][col] : total - grid[2][col];
    return total;
  });

  return { grid, rowOps, colOps, rowTotals, colTotals };
}

function ByTheNumbers() {
  const [puzzle, setPuzzle] = useState<GridConfig>(() => generateRandomPuzzle());
  const [grid, setGrid] = useState<Cell[][]>([]);
  const [availableFacts, setAvailableFacts] = useState<Fact[]>([]);
  const [selectedFact, setSelectedFact] = useState<Fact | null>(null);
  const [moveCount, setMoveCount] = useState(0);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [gameComplete, setGameComplete] = useState(false);
  const [gameFailed, setGameFailed] = useState(false);
  const [rowResults, setRowResults] = useState<boolean[]>([false, false, false]);
  const [colResults, setColResults] = useState<boolean[]>([false, false, false]);
  const [modalOpen, setModalOpen] = useState(false);

  // Initialize game
  React.useEffect(() => {
    initializeGame();
    // eslint-disable-next-line
  }, []);

  // Timer
  React.useEffect(() => {
    if (startTime && !gameComplete && !gameFailed) {
      const interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [startTime, gameComplete, gameFailed]);

  const initializeGame = () => {
    const newPuzzle = generateRandomPuzzle();
    setPuzzle(newPuzzle);

    const allPositions = Array.from({ length: 9 }, (_, i) => i);
    const positions = allPositions.sort(() => Math.random() - 0.5).slice(0, 3);

    const newGrid: Cell[][] = [];
    const factsNeeded: number[] = [];

    for (let row = 0; row < 3; row++) {
      newGrid[row] = [];
      for (let col = 0; col < 3; col++) {
        const pos = row * 3 + col;
        const value = newPuzzle.grid[row][col];

        if (positions.includes(pos)) {
          const factOptions = FACTS_DATABASE[value];
          const randomFact = factOptions[Math.floor(Math.random() * factOptions.length)];

          newGrid[row][col] = {
            value,
            isPreFilled: true,
            factText: randomFact,
          };
        } else {
          newGrid[row][col] = {
            value: null,
            isPreFilled: false,
          };
          factsNeeded.push(value);
        }
      }
    }

    const facts: Fact[] = factsNeeded.map((num, index) => {
      const factOptions = FACTS_DATABASE[num];
      const randomFact = factOptions[Math.floor(Math.random() * factOptions.length)];

      return {
        id: `fact-${index}`,
        number: num,
        text: randomFact,
        isPlaced: false,
      };
    });

    const shuffledFacts = facts.sort(() => Math.random() - 0.5);

    setGrid(newGrid);
    setAvailableFacts(shuffledFacts);
    setSelectedFact(null);
    setMoveCount(0);
    setStartTime(null);
    setElapsedTime(0);
    setGameComplete(false);
    setGameFailed(false);
    setRowResults([false, false, false]);
    setColResults([false, false, false]);
    setModalOpen(false);
  };

  const calculateRowTotal = (row: number, currentGrid: Cell[][]): number => {
    const values = currentGrid[row];
    let total = values[0].value || 0;
    const op1 = puzzle.rowOps[row * 2];
    const op2 = puzzle.rowOps[row * 2 + 1];
    total = op1 === "+" ? total + (values[1].value || 0) : total - (values[1].value || 0);
    total = op2 === "+" ? total + (values[2].value || 0) : total - (values[2].value || 0);
    return total;
  };

  const calculateColTotal = (col: number, currentGrid: Cell[][]): number => {
    let total = currentGrid[0][col].value || 0;
    const op1 = puzzle.colOps[col * 2];
    const op2 = puzzle.colOps[col * 2 + 1];
    total =
      op1 === "+"
        ? total + (currentGrid[1][col].value || 0)
        : total - (currentGrid[1][col].value || 0);
    total =
      op2 === "+"
        ? total + (currentGrid[2][col].value || 0)
        : total - (currentGrid[2][col].value || 0);
    return total;
  };

  const checkResults = (currentGrid: Cell[][]) => {
    const newRowResults = [false, false, false];
    const newColResults = [false, false, false];
    let allCorrect = true;

    for (let row = 0; row < 3; row++) {
      const rowComplete = currentGrid[row].every((cell) => cell.value !== null);
      if (rowComplete) {
        const calculatedTotal = calculateRowTotal(row, currentGrid);
        const expectedTotal = puzzle.rowTotals[row];
        newRowResults[row] = calculatedTotal === expectedTotal;
      } else {
        allCorrect = false;
      }
    }

    for (let col = 0; col < 3; col++) {
      const colComplete = [0, 1, 2].every((row) => currentGrid[row][col].value !== null);
      if (colComplete) {
        const calculatedTotal = calculateColTotal(col, currentGrid);
        const expectedTotal = puzzle.colTotals[col];
        newColResults[col] = calculatedTotal === expectedTotal;
      }
    }

    setRowResults(newRowResults);
    setColResults(newColResults);

    if (allCorrect && newRowResults.every((r) => r) && newColResults.every((c) => c)) {
      setGameComplete(true);
      setModalOpen(true);
    }
  };

  const handleFactClick = (fact: Fact) => {
    if (fact.isPlaced) return;
    if (!startTime) setStartTime(Date.now());
    setSelectedFact(selectedFact?.id === fact.id ? null : fact);
  };

  const handleCellClick = (row: number, col: number) => {
    if (grid[row][col].isPreFilled || gameFailed || gameComplete) return;
    if (!startTime) setStartTime(Date.now());

    if (selectedFact) {
      const newGrid = grid.map((r) => r.map((c) => ({ ...c })));
      const newFacts = [...availableFacts];

      if (newGrid[row][col].value !== null) {
        const oldFactText = newGrid[row][col].factText;
        const oldFact = newFacts.find((f) => f.text === oldFactText);
        if (oldFact) oldFact.isPlaced = false;
      }

      newGrid[row][col].value = selectedFact.number;
      newGrid[row][col].factText = selectedFact.text;

      const factIndex = newFacts.findIndex((f) => f.id === selectedFact.id);
      if (factIndex !== -1) newFacts[factIndex].isPlaced = true;

      const newMoveCount = moveCount + 1;
      setGrid(newGrid);
      setAvailableFacts(newFacts);
      setSelectedFact(null);
      setMoveCount(newMoveCount);

      if (newMoveCount >= 12) {
        const allFilled = newGrid.every((row) => row.every((cell) => cell.value !== null));
        if (!allFilled) {
          setGameFailed(true);
          setModalOpen(true);
          return;
        }
      }

      checkResults(newGrid);
    } else if (grid[row][col].value !== null && grid[row][col].factText) {
      const newGrid = grid.map((r) => r.map((c) => ({ ...c })));
      const newFacts = [...availableFacts];

      const fact = newFacts.find((f) => f.text === grid[row][col].factText);
      if (fact) {
        newGrid[row][col].value = null;
        newGrid[row][col].factText = undefined;
        fact.isPlaced = false;

        setGrid(newGrid);
        setAvailableFacts(newFacts);
        setSelectedFact(fact);

        checkResults(newGrid);
      }
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 py-8 px-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold text-purple-600 mb-2">By The Numbers</h1>
          <p className="text-gray-600">Math meets trivia</p>
        </div>

        {/* Stats */}
        <div className="flex justify-between items-center mb-4 bg-white rounded-lg p-3 shadow-sm">
          <div className="text-sm">
            <span className="text-gray-600">Moves: </span>
            <span className="font-bold text-purple-600">{moveCount}/12</span>
          </div>
          <div className="text-sm">
            <span className="text-gray-600">Time: </span>
            <span className="font-bold text-purple-600">{formatTime(elapsedTime)}</span>
          </div>
        </div>

        {/* Grid */}
        <div className="bg-white rounded-lg p-4 shadow-md mb-4">
          <div className="flex justify-center">
            <div className="inline-block">
              <div
                className="grid gap-1 mb-1.5"
                style={{
                  gridTemplateColumns: "60px 30px 60px 30px 60px 30px 40px",
                  alignItems: "center",
                  justifyItems: "center",
                }}
              >
                {grid.map((row, rowIndex) => (
                  <React.Fragment key={rowIndex}>
                    {row.map((cell, colIndex) => (
                      <React.Fragment key={`${rowIndex}-${colIndex}`}>
                        <div
                          onClick={() => handleCellClick(rowIndex, colIndex)}
                          className={`
                            w-[60px] h-[60px] rounded-md flex items-center justify-center font-bold transition-all
                            ${
                              cell.isPreFilled
                                ? "bg-gradient-to-br from-purple-600 to-purple-700 text-white cursor-default border-2 border-purple-600"
                                : cell.value === null
                                  ? "border-2 border-dashed border-gray-300 bg-gray-50 cursor-pointer hover:border-purple-400"
                                  : "border-2 border-gray-200 bg-white cursor-pointer"
                            }
                            ${!cell.isPreFilled && selectedFact ? "hover:scale-105" : ""}
                          `}
                        >
                          {cell.isPreFilled ? (
                            <div className="flex flex-col items-center justify-center gap-0.5">
                              <div className="text-[8px] leading-tight text-center font-semibold">
                                {cell.factText}
                              </div>
                              {gameComplete && (
                                <div className="text-lg font-bold">{cell.value}</div>
                              )}
                            </div>
                          ) : cell.factText ? (
                            <div className="flex flex-col items-center justify-center gap-0.5">
                              <div className="text-[8px] leading-tight text-center text-gray-600">
                                {cell.factText}
                              </div>
                              {gameComplete && (
                                <div className="text-lg font-bold">{cell.value}</div>
                              )}
                            </div>
                          ) : null}
                        </div>
                        {colIndex < 2 && (
                          <div className="text-lg font-bold text-gray-400 text-center">
                            {puzzle.rowOps[rowIndex * 2 + colIndex]}
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                    <div className="text-lg font-bold text-gray-400 text-center">=</div>
                    <div
                      className={`w-10 h-10 rounded-md flex items-center justify-center text-white font-bold text-lg transition-all ${rowResults[rowIndex] ? "bg-green-500 shadow-lg shadow-green-200" : "bg-red-500"}`}
                    >
                      {puzzle.rowTotals[rowIndex]}
                    </div>

                    {rowIndex < 2 && (
                      <div
                        className="col-span-7 grid gap-1 h-4"
                        style={{
                          gridTemplateColumns: "60px 30px 60px 30px 60px 30px 40px",
                          alignItems: "center",
                          justifyItems: "center",
                        }}
                      >
                        <div className="text-sm font-bold text-gray-400">
                          {puzzle.colOps[0 * 2 + rowIndex]}
                        </div>
                        <div />
                        <div className="text-sm font-bold text-gray-400">
                          {puzzle.colOps[1 * 2 + rowIndex]}
                        </div>
                        <div />
                        <div className="text-sm font-bold text-gray-400">
                          {puzzle.colOps[2 * 2 + rowIndex]}
                        </div>
                        <div />
                        <div />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Column Totals */}
              <div className="space-y-1">
                <div
                  className="grid gap-1"
                  style={{
                    gridTemplateColumns: "60px 30px 60px 30px 60px",
                    justifyItems: "center",
                  }}
                >
                  <div className="text-sm font-bold text-gray-400">=</div>
                  <div />
                  <div className="text-sm font-bold text-gray-400">=</div>
                  <div />
                  <div className="text-sm font-bold text-gray-400">=</div>
                </div>
                <div
                  className="grid gap-1"
                  style={{
                    gridTemplateColumns: "60px 30px 60px 30px 60px",
                    justifyItems: "center",
                  }}
                >
                  {[0, 1, 2].map((col) => (
                    <React.Fragment key={col}>
                      <div
                        className={`w-10 h-10 rounded-md flex items-center justify-center text-white font-bold text-lg transition-all ${colResults[col] ? "bg-green-500 shadow-lg shadow-green-200" : "bg-red-500"}`}
                      >
                        {puzzle.colTotals[col]}
                      </div>
                      {col < 2 && <div />}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Facts Bank */}
        <div className="bg-white rounded-lg p-4 shadow-md">
          <h3 className="text-lg font-semibold text-center mb-3">Available Facts:</h3>
          <div className="grid grid-cols-3 gap-2.5 justify-items-center">
            {availableFacts.map((fact) => (
              <div
                key={fact.id}
                onClick={() => handleFactClick(fact)}
                className={`
                  w-[60px] h-[60px] rounded-md flex items-center justify-center p-1 transition-all cursor-pointer
                  ${
                    selectedFact?.id === fact.id
                      ? "border-2 border-purple-600 bg-purple-50"
                      : "border-2 border-gray-300 bg-white"
                  }
                  ${fact.isPlaced ? "opacity-50 cursor-not-allowed bg-gray-100" : "hover:scale-105"}
                `}
              >
                <div className="text-[8px] leading-tight text-center text-gray-600">
                  {fact.text}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Modal */}
        {modalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-sm w-full">
              <h2 className="text-2xl font-bold text-center mb-4">
                {gameFailed ? "😞 Game Over" : "🎉 Puzzle Complete!"}
              </h2>
              {gameFailed ? (
                <div className="text-center mb-6">
                  <p className="text-lg mb-2">You've reached the 12 move limit!</p>
                  <p className="text-gray-600">Try again with a new puzzle.</p>
                </div>
              ) : (
                <div className="text-center mb-6">
                  <p className="text-lg">
                    Time:{" "}
                    <span className="font-bold text-purple-600">{formatTime(elapsedTime)}</span>
                  </p>
                  <p className="text-lg">
                    Moves: <span className="font-bold text-purple-600">{moveCount}</span>
                  </p>
                </div>
              )}
              <button
                onClick={initializeGame}
                className="w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white font-semibold py-3 px-6 rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all"
              >
                {gameFailed ? "Try Again" : "Play Again"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ByTheNumbers;
